/**
 * useArTableExport Hook
 *
 * Orchestrates XLSX export for ArTable.
 * When pagination is active (totalPages > 1), calls exportArTableRows CF
 * to get all rows. Otherwise reads from Valtio store.
 *
 * Holds NO form subscription. The form number, version number, form type and
 * split query are consumed only when the user exports, so the form is read once
 * with `snapshot(formStore.state)` inside the handler. It previously took them
 * from `useFormStore()`, whose spread of `formVersion.data` re-rendered every
 * consumer — including `ArTableContextMenu`, which mounts this hook per table —
 * on each keystroke in the form (SYD-2264).
 *
 * Row source, split, column filters and the bundle rules all come from
 * `resolveArTableVisibleRows` — the same resolver every ArTable workflow run
 * scopes itself with, so this sheet and a run can never disagree about what is
 * visible (SYD-2322). Inside it, bundle member rows and cells are withheld and
 * header rows identified by `resolveArTableBundleExportVisibility` (SYD-2279/2276).
 * The email/form XLSX snapshot shares that bundle pass and nothing above it: it
 * resolves its own split over its own narrowed columns, so it is not bound to agree
 * with this sheet on which rows a split leaves standing.
 *
 * The ticked selection is intersected into the resolver's output afterwards,
 * never before it: classifying a selection subset orphans members whose header is
 * unticked. That narrowing is export-only and stays here.
 *
 * Outside the internal view it runs `resolveExportVisibilityGate`, the predicate
 * the other two XLSX producers share: the active view's `hideKitComponents` /
 * `hideGroupComponents` flags live on `parameters/organization`, so a listener
 * that failed before delivering must refuse rather than emit member rows a
 * customer-facing view withholds.
 *
 * Neither row source seeds from `uiState.renderedRowIds`: it has collapsed
 * bundles removed, and collapse is a per-session viewport affordance that the
 * PDF, email XLSX and paginated paths all export through (SYD-2279).
 *
 * The resolver's `isTruncated` is deliberately not honoured here: a sheet capped at
 * `AR_TABLE_MAX_ROWS` is this export's long-standing behaviour. Only the workflow row
 * scope fails closed on it, because there a partial list is acted on, not read.
 */

import { useState, useCallback, useContext } from 'react';
import { snapshot } from 'valtio';
import { MessageContext } from '@/providers/MessageContext';
import { formStore } from '../../../store';
import { useVisibilityMode } from '@/use/useVisibilityMode';
import { FieldTypeContext } from '@/providers/FieldTypeContext';
import { useStoreNonReactive } from '@/store/useStoreNonReactive';
import { ArTableUIStateContext } from '../context';
import { exportArTableToExcel } from './exportArTableToExcel';
import type { DocumentState } from '@/store/Store.types';
import type { Snapshot } from 'valtio';
import type {
  ArTableType,
  ArColumnDefinition,
  ArRowId,
  TypeId,
  ArTableId,
} from '#types/System.Artifacts.types';
import { createArFormulaEngineConfig } from '#types';
import type { OrderByOption } from '@/store/Store.types';
import type { SplitQuery, UIArTableColumnOverride } from '#types/System.types';
import { applyColumnConfig } from '@/components/ArTable/column/applyColumnConfig';
import resolveArColumnLabel from '../column/resolveArColumnLabel';
import { buildCompositeValueFormatter } from './buildCompositeValueFormatter';
import { filterArRowIdsBySelection } from './filterArRowIdsBySelection';
import { resolveArTableVisibleRows } from '../visibleRows';
import { useOrganizationParameters } from '@/use/useOrganizationParameters';
import { resolveExportVisibilityGate } from '@/use/resolveExportVisibilityGate';
import { resolveKitRowColumnCalc } from '@/store/arTableKitRowCalc';

type UseArTableExportParams = {
  tableId: ArTableId;
  parentPath: string;
  rowsOrderBy: OrderByOption;
  columnConfig?: UIArTableColumnOverride[];
};

type UseArTableExportReturn = {
  handleExportXlsx: () => Promise<void>;
  exportingXlsx: boolean;
  canExport: boolean;
};

/**
 * Hook for ArTable XLSX export functionality.
 * Uses imperative snapshot reads to avoid re-renders on data changes.
 */
function useArTableExport({ tableId, parentPath, rowsOrderBy, columnConfig }: UseArTableExportParams): UseArTableExportReturn {
  const [exportingXlsx, setExportingXlsx] = useState(false);

  const { messageApi } = useContext(MessageContext);
  const visibilityMode = useVisibilityMode();
  const { fieldTypes } = useContext(FieldTypeContext);
  const uiState = useContext(ArTableUIStateContext);
  const {
    data: orgParams,
    loading: orgParamsLoading,
    error: orgParamsError,
    getVisibilityTypesForFormType,
  } = useOrganizationParameters();

  const { state: schemaState } = useStoreNonReactive('schema');
  const { state: rowsState } = useStoreNonReactive('rows');

  const getColumnLabel = useCallback(
    (column: ArColumnDefinition): string =>
      resolveArColumnLabel(column, fieldTypes),
    [fieldTypes],
  );

  const handleExportXlsx = useCallback(async () => {
    const gate = resolveExportVisibilityGate({ visibilityMode, orgParams, orgParamsLoading, orgParamsError });
    if (gate) {
      messageApi[gate.severity](gate.message);
      return;
    }

    const live = snapshot(formStore.state);
    const formNumber = live.form.data?.formNumber;
    const formVersionNumber = live.formVersion.data?.formVersionNumber;
    const formLabel = formNumber && formVersionNumber !== undefined
      ? `${formNumber}-v${formVersionNumber}`
      : formNumber;
    const visibilityType = getVisibilityTypesForFormType(live.formType.data?.docId)
      .find((v) => v.id === visibilityMode);

    const schemaSnap = snapshot(schemaState) as Snapshot<DocumentState<ArTableType>>;
    const uiSnap = uiState
      ? snapshot(uiState)
      : { renderedRowIds: [] as ArRowId[], pagination: { totalPages: 0, totalRows: 0 } };

    /** Fast path only: nothing on screen means an empty table or no `uiState`. */
    if (uiSnap.renderedRowIds.length === 0) {
      messageApi.warning('No rows to export');
      return;
    }

    setExportingXlsx(true);

    try {
      /**
       * `allColumns` decides which rows are visible and `columns` which cells the
       * sheet carries: the grid resolves the split over the table type's own
       * columns, so a layout's `columnConfig` must not widen or narrow the row set.
       */
      const allColumns = (schemaSnap.data?.columns ?? []) as ArColumnDefinition[];
      const columns = columnConfig ? applyColumnConfig(allColumns, columnConfig) : allColumns;
      /**
       * The sheet builder evaluates formulas over whichever rows it is handed, so
       * the paginated Cloud Function's rows — which carry no derived value and
       * never passed through the grid — export the same numbers as the store's.
       */
      const formulaConfig = createArFormulaEngineConfig(schemaSnap.data as ArTableType | undefined);
      const isPaginated = uiSnap.pagination.totalPages > 1;
      if (isPaginated) messageApi.info('Exporting all rows...');

      const filterEntries = Object.entries(uiState ? snapshot(uiState.filtering).byColumnId : {}).filter(
        (entry): entry is [TypeId, string] =>
          typeof entry[1] === 'string' && entry[1].length > 0,
      );

      const resolved = await resolveArTableVisibleRows({
        tableId,
        parentPath,
        rowsOrderBy,
        isPaginated,
        totalRows: uiSnap.pagination.totalRows,
        rowsState,
        columns: allColumns,
        suppressionColumns: columns,
        splitQuery: live.splitQuery as SplitQuery | undefined,
        filterEntries,
        visibilityMode,
        visibilityType,
        kitsEnabled: schemaSnap.data?.bundles?.kits?.enabled === true,
        groupsEnabled: schemaSnap.data?.bundles?.groups === true,
      });
      const rowsMap = resolved.rowsMap;

      /**
       * Narrowed to the ticked selection only after the resolver has classified,
       * which the interactive grid never does against a selection. `ArTableBody`
       * does narrow by `pdfSelectedRowIds` before classifying, so a member ticked
       * without its header survives here and is dropped from the PDF (SYD-2279).
       */
      const selectedRowIds = uiState
        ? (snapshot(uiState.selection).selectedRowIds as ArRowId[])
        : null;
      const visibleRowIds = filterArRowIdsBySelection({
        rowIds: resolved.visibleRowIds,
        selectedRowIds,
      });

      if (visibleRowIds.length === 0) {
        messageApi.warning('No rows to export after filters');
        return;
      }

      /**
       * Through `resolveKitRowColumnCalc`, never the table config alone: it merges
       * the form type's rules and the global defaults in, as `ArTableBody` does.
       */
      const kitCalcDriverTypeIds = new Set<TypeId>(
        resolveKitRowColumnCalc(
          schemaSnap.data?.kitRowColumnCalc,
          live.formType.data?.kitRowColumnCalc,
        ).map((calc) => calc.typeId),
      );

      const today = new Date().toISOString().split('T')[0];
      const tableLabel = schemaSnap.data?.description?.shortLabel ?? 'table';
      const sanitize = (str: string) => str.replace(/[^a-zA-Z0-9-_]/g, '-');
      const filename = formLabel
        ? `${sanitize(formLabel)}-${sanitize(tableLabel)}-${today}.xlsx`
        : `${sanitize(tableLabel)}-${today}.xlsx`;

      const formatCompositeValue = await buildCompositeValueFormatter(columns, fieldTypes);

      const result = await exportArTableToExcel({
        columns,
        rowsMap,
        visibleRowIds,
        filename,
        sheetName: tableLabel.substring(0, 31),
        getColumnLabel,
        isCellSuppressed: resolved.isCellSuppressed,
        bundleTypeByHeaderRowId: resolved.bundleTypeByHeaderRowId,
        kitCalcDriverTypeIds,
        formulaConfig,
        visibilityMode,
        fieldTypes,
        formatCompositeValue,
      });

      if (result.success) {
        messageApi.success(result.message);
      } else {
        messageApi.warning(result.message);
      }
    } catch (error) {
      console.error('ArTable export failed:', error);
      messageApi.error('Export failed. Please try again.');
    } finally {
      setExportingXlsx(false);
    }
  }, [schemaState, rowsState, uiState, parentPath, tableId, rowsOrderBy, columnConfig, getColumnLabel, messageApi, visibilityMode, fieldTypes, getVisibilityTypesForFormType, orgParamsLoading, orgParams, orgParamsError]);

  return {
    handleExportXlsx,
    exportingXlsx,
    canExport: true,
  };
}

export { useArTableExport };
export type { UseArTableExportReturn, UseArTableExportParams };
